import React, { useEffect, useState } from "react";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useNavigate, useParams } from "react-router-dom";

import MarketingHero from "components/marketing/MarketingHero";
import CenterLoading from "../../components/core/CenterLoading";
import { getAutocompleteType } from "../../components/contact/ContactForm";
import users, { UserDetailRes } from "services/users";
import { useTitle } from "utils/UseTitleUtils";

interface AccountSetupProps {}
type AccountSetupParams = {
  user_id?: string;
};

export type SetupAccount = {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  ppAccepted?: boolean;
  tcAccepted?: boolean;
  accountSetupToken?: string;
};

type SetupAccountError = {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  ppAccepted?: string;
  tcAccepted?: string;
  accountSetupToken?: string;
};

const defaultInput: SetupAccount = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  ppAccepted: false,
  tcAccepted: false,
  accountSetupToken: "",
};

const AccountSetup = (props: AccountSetupProps) => {
  const [inputs, setInputs] = useState<SetupAccount>(defaultInput);
  const [errors, setErrors] = useState<SetupAccountError>({});
  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const params = useParams<AccountSetupParams>();
  const navigate = useNavigate();

  useTitle("Employee Account Setup");

  const setUserDetails = async () => {
    setIsLoading(true);
    try {
      await users.setUserSetup(inputs);
      navigate("/auth/login", {
        replace: true,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserDetail = async () => {
    if (!params.user_id) {
      return;
    }

    setIsLoading(true);
    try {
      const getUserDetailRes: UserDetailRes = await users.getUserSetup(
        params.user_id
      );
      setInputs({
        firstName: getUserDetailRes?.firstName ?? "",
        lastName: getUserDetailRes?.lastName ?? "",
        email: getUserDetailRes?.email ?? "",
        accountSetupToken: getUserDetailRes.accountSetupToken ?? "",
        password: "",
        confirmPassword: "",
        ppAccepted: false,
        tcAccepted: false,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const errorMessage = (field: keyof SetupAccountError) => {
    if (!errors[field]) {
      return null;
    }

    return <div className="marketing-page__error">{errors[field]}</div>;
  };

  const handleChange =
    (fieldName: keyof SetupAccount) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputs((current) => ({
        ...current,
        [fieldName]: event.target.value,
      }));
      setErrors((current) => {
        const nextErrors = { ...current };
        if (fieldName === "password" || fieldName === "confirmPassword") {
          delete nextErrors.password;
          delete nextErrors.confirmPassword;
        } else {
          delete nextErrors[fieldName];
        }
        return nextErrors;
      });
    };

  const onSetupAccount = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: SetupAccountError = {};

    if (!inputs.firstName?.trim()) {
      nextErrors.firstName = "First name is required.";
    }

    if (!inputs.lastName?.trim()) {
      nextErrors.lastName = "Last name is required.";
    }

    if (!inputs.password?.trim()) {
      nextErrors.password = "Password is required.";
    }

    if (!inputs.confirmPassword?.trim()) {
      nextErrors.confirmPassword = "Confirm password is required.";
    } else if (inputs.password?.trim() !== inputs.confirmPassword?.trim()) {
      nextErrors.confirmPassword = "Passwords do not match.";
    }

    if (!inputs.ppAccepted) {
      nextErrors.ppAccepted = "Please accept the Privacy Policy.";
    }

    if (!inputs.tcAccepted) {
      nextErrors.tcAccepted = "Please accept the Terms and Conditions.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setUserDetails();
  };

  useEffect(() => {
    if (params.user_id) {
      getUserDetail();
    }
  }, []);

  return (
    <div className="marketing-page marketing-page--account-setup">
      <CenterLoading isVisible={isLoading} />

      <main className="marketing-page__main">
        <MarketingHero title="Employee Account Setup" />

        <section className="marketing-page__section">
          <div className="marketing-page__section-inner">
            <div className="marketing-page__content-grid">
              <div className="marketing-page__form-panel">
                <div className="marketing-page__copy-stack">
                  <p>
                    Confirm the details your employer has provided and create
                    your password.
                  </p>
                </div>

                <form className="marketing-page__form" onSubmit={onSetupAccount}>
                  <div className="marketing-page__split-fields">
                    <div className="marketing-page__field">
                      <label
                        className="marketing-page__label"
                        htmlFor="setup-first-name"
                      >
                        First Name
                      </label>
                      <input
                        id="setup-first-name"
                        className="marketing-page__input"
                        type="text"
                        required
                        autoComplete={getAutocompleteType("firstName")}
                        value={inputs.firstName}
                        onChange={handleChange("firstName")}
                      />
                      {errorMessage("firstName")}
                    </div>

                    <div className="marketing-page__field">
                      <label
                        className="marketing-page__label"
                        htmlFor="setup-last-name"
                      >
                        Last Name
                      </label>
                      <input
                        id="setup-last-name"
                        className="marketing-page__input"
                        type="text"
                        required
                        autoComplete={getAutocompleteType("lastName")}
                        value={inputs.lastName}
                        onChange={handleChange("lastName")}
                      />
                      {errorMessage("lastName")}
                    </div>
                  </div>

                  <div className="marketing-page__field">
                    <label className="marketing-page__label" htmlFor="setup-email">
                      Email Address
                    </label>
                    <input
                      id="setup-email"
                      className="marketing-page__input"
                      type="email"
                      value={inputs.email}
                      disabled
                    />
                  </div>

                  <div className="marketing-page__split-fields">
                    <div className="marketing-page__field">
                      <label
                        className="marketing-page__label"
                        htmlFor="setup-password"
                      >
                        Password
                      </label>
                      <div className="input-group">
                        <input
                          id="setup-password"
                          className="marketing-page__input form-control"
                          type={passwordVisible ? "text" : "password"}
                          required
                          autoComplete={getAutocompleteType("password")}
                          value={inputs.password}
                          onChange={handleChange("password")}
                        />
                        <button
                          className="input-group-text"
                          aria-label="Toggle password visibility"
                          title="toggle visibility"
                          type="button"
                          onClick={() =>
                            setPasswordVisible((current) => !current)
                          }
                        >
                          <FontAwesomeIcon
                            icon={passwordVisible ? faEyeSlash : faEye}
                          />
                        </button>
                      </div>
                      {errorMessage("password")}
                    </div>

                    <div className="marketing-page__field">
                      <label
                        className="marketing-page__label"
                        htmlFor="setup-confirm-password"
                      >
                        Confirm Password
                      </label>
                      <div className="input-group">
                        <input
                          id="setup-confirm-password"
                          className="marketing-page__input form-control"
                          type={confirmPasswordVisible ? "text" : "password"}
                          required
                          autoComplete={getAutocompleteType("confirmPassword")}
                          value={inputs.confirmPassword}
                          onChange={handleChange("confirmPassword")}
                        />
                        <button
                          className="input-group-text"
                          aria-label="Toggle confirm password visibility"
                          title="toggle visibility"
                          type="button"
                          onClick={() =>
                            setConfirmPasswordVisible((current) => !current)
                          }
                        >
                          <FontAwesomeIcon
                            icon={confirmPasswordVisible ? faEyeSlash : faEye}
                          />
                        </button>
                      </div>
                      {errorMessage("confirmPassword")}
                    </div>
                  </div>

                  <div className="marketing-page__checkbox-stack">
                    <label className="marketing-page__checkbox-inline" htmlFor="setup-privacy">
                      <input
                        id="setup-privacy"
                        type="checkbox"
                        checked={Boolean(inputs.ppAccepted)}
                        onChange={(event) =>
                          setInputs((current) => ({
                            ...current,
                            ppAccepted: event.target.checked,
                          }))
                        }
                      />
                      <span>
                        I have read and agree to the{" "}
                        <Link to="/privacy-policy" target="_blank" rel="noreferrer">
                          Privacy Policy
                        </Link>
                      </span>
                    </label>
                    {errorMessage("ppAccepted")}

                    <label className="marketing-page__checkbox-inline" htmlFor="setup-terms">
                      <input
                        id="setup-terms"
                        type="checkbox"
                        checked={Boolean(inputs.tcAccepted)}
                        onChange={(event) =>
                          setInputs((current) => ({
                            ...current,
                            tcAccepted: event.target.checked,
                          }))
                        }
                      />
                      <span>
                        I have read and agree to the{" "}
                        <Link to="/terms" target="_blank" rel="noreferrer">
                          Terms and Conditions
                        </Link>
                      </span>
                    </label>
                    {errorMessage("tcAccepted")}
                  </div>

                  <button
                    title="next"
                    aria-label="Next Step"
                    type="submit"
                    className="marketing__button marketing__button--primary marketing-page__full-button"
                    disabled={isLoading}
                  >
                    Next
                  </button>
                </form>
              </div>

              <aside className="marketing-page__sidebar">
                <h2 className="marketing-page__sidebar-title">
                  Before you continue
                </h2>
                <div className="marketing-page__sidebar-items">
                  <div className="marketing-page__sidebar-item">
                    <p>
                      Confirm your name and email address before setting your
                      password.
                    </p>
                  </div>
                  <div className="marketing-page__sidebar-item">
                    <p>
                      Once complete, you&apos;ll be taken to login and can start
                      using insite.work.
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AccountSetup;
